// src/hooks/order/useOrderPaymentSync.ts
// Hook para sincronizar payment_method con forma_pago y manejar pagos parciales

import { useEffect } from 'react'
import type { OrderData, PagoParcial } from '../../types/orderFormTypes'
import { getDefaultOrderStatusByPaymentMethod } from '../../utils/orderDefaultStatusByPayment'

interface UseOrderPaymentSyncProps {
  formData: OrderData
  mode: 'view' | 'edit' | 'create'
  pagosParciales: PagoParcial[]
  handleInputChange: (section: keyof OrderData, field: string, value: any) => void
  agregarPagoParcial: () => void
  userRoleId?: number | null
}

export function useOrderPaymentSync({
  formData,
  mode,
  pagosParciales,
  handleInputChange,
  agregarPagoParcial,
  userRoleId
}: UseOrderPaymentSyncProps) {
  // Sincronizar forma de pago con método de pago
  useEffect(() => {
    if (formData.extraDetails?.payment_method) {
      let formaPagoValue = '6' // Por defecto contado
      const pm = formData.extraDetails.payment_method.toString().trim().toLowerCase()

      if (pm === 'dataf') {
        formaPagoValue = '6' // Datafono: Contado
      } else if (pm === 'cod') {
        formaPagoValue = '4' // Contra entrega
      } else if (
        pm === 'cmdm' ||
        pm === 'addi' ||
        pm === 'plan_separe'
      ) {
        formaPagoValue = '4' // Crédito
      } else if (pm === 'bold') {
        formaPagoValue = '6' // Contado (para bold)
      } else if (pm === 'part') {
        const tieneCredito = pagosParciales.some(pago => 
          pago.metodo_pago === 'cod' || pago.metodo_pago === 'cmdm' || pago.metodo_pago === 'addi'
        )
        formaPagoValue = tieneCredito ? '4' : '6'
      }
      
      // Solo actualizar si el valor es diferente para evitar loops infinitos
      const formaPagoActual = formData.extraDetails?.forma_pago || '6'
      if (formaPagoActual !== formaPagoValue) {
        console.log(`🔄 Sincronizando forma_pago: ${formaPagoActual} → ${formaPagoValue} (payment_method: ${formData.extraDetails.payment_method})`)
        handleInputChange('extraDetails', 'forma_pago', formaPagoValue)
      }
      
      // En create + contraentrega, fijar plazo a 15 días
      if (pm === 'cod' && mode === 'create') {
        const plazoActual = String(formData.extraDetails?.plazo || '')
        if (plazoActual !== '15') {
          handleInputChange('extraDetails', 'plazo', '15')
        }
      }

      // En ADDI, el plazo siempre es 30 días
      if (pm === 'addi') {
        const plazoActual = String(formData.extraDetails?.plazo || '')
        if (plazoActual !== '30') {
          handleInputChange('extraDetails', 'plazo', '30')
        }
      }

      // No sobrescribir status en edit/view: debe mostrarse el estado real del pedido.
      if (mode === 'create' && pm) {
        const defaultStatus = getDefaultOrderStatusByPaymentMethod(pm, userRoleId)
        const statusActual = (formData.status || '').toString().trim().toLowerCase()
        if (statusActual !== defaultStatus) {
          handleInputChange('status', '', defaultStatus)
        }
      }

      // Si se selecciona "part" en modo create y no hay pagos, agregar uno inicial
      if (
        (pm === 'part' || pm === 'plan_separe') &&
        mode === 'create' &&
        pagosParciales.length === 0
      ) {
        agregarPagoParcial()
      }
    } else if (formData.extraDetails?.forma_pago && !formData.extraDetails?.payment_method) {
      // Si hay forma_pago pero no payment_method, establecer forma_pago a '6' por defecto
      if (formData.extraDetails.forma_pago !== '6') {
        handleInputChange('extraDetails', 'forma_pago', '6')
      }
    }
  }, [
    formData.extraDetails?.payment_method,
    formData.extraDetails?.forma_pago,
    formData.extraDetails?.plazo,
    formData.status,
    pagosParciales,
    mode,
    userRoleId,
    handleInputChange,
    agregarPagoParcial
  ])
}

